import { useState } from "react";
import { Button } from "@/components/ui/button";
import { PasswordInput } from "@/components/ui/password-input";
import { KeyRound, CheckCircle, AlertCircle } from "lucide-react";

interface PasswordChangeProps {
  currentPassword: string;
  onSuccess: () => void;
  onCancel?: () => void;
}

export default function PasswordChange({ currentPassword, onSuccess, onCancel }: PasswordChangeProps) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Password requirements
  const requirements = [
    { label: "At least 8 characters", met: newPassword.length >= 8 },
    { label: "Contains a number", met: /\d/.test(newPassword) },
    { label: "Contains a letter", met: /[a-zA-Z]/.test(newPassword) },
  ];

  const allRequirementsMet = requirements.every(r => r.met);
  const passwordsMatch = newPassword === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!allRequirementsMet) {
      setError("Please meet all password requirements");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      const res = await fetch("/api/auth/change-password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          currentPassword,
          newPassword,
        }),
      });

      if (res.ok) {
        onSuccess();
      } else {
        const data = await res.json().catch(() => ({}));
        setError(data.message || "Failed to change password");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-950">
      <div className="bg-white dark:bg-gray-900 p-8 rounded shadow-md w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <KeyRound className="h-12 w-12 text-[#1737A2] dark:text-blue-400 mb-2" />
          <h2 className="text-2xl font-bold text-[#1737A2] dark:text-blue-400 mb-1">Change Your Password</h2>
          <p className="text-gray-500 dark:text-gray-400 text-sm text-center">
            For security, please create a new password to replace your temporary one.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              New Password
            </label>
            <PasswordInput
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1737A2]"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Confirm New Password
            </label>
            <PasswordInput
              className="w-full border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#1737A2]"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </div>

          {/* Password requirements */}
          <div className="bg-gray-50 dark:bg-gray-800 p-3 rounded-md space-y-2">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Password requirements:</p>
            {requirements.map((req, i) => (
              <div key={i} className="flex items-center gap-2 text-xs">
                {req.met ? (
                  <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-500" />
                ) : (
                  <AlertCircle className="h-3.5 w-3.5 text-gray-400" />
                )}
                <span className={req.met ? "text-green-700 dark:text-green-400" : "text-gray-500 dark:text-gray-400"}>
                  {req.label}
                </span>
              </div>
            ))}
            <div className="flex items-center gap-2 text-xs mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
              {passwordsMatch ? (
                <CheckCircle className="h-3.5 w-3.5 text-green-600 dark:text-green-500" />
              ) : (
                <AlertCircle className="h-3.5 w-3.5 text-gray-400" />
              )}
              <span className={passwordsMatch ? "text-green-700 dark:text-green-400" : "text-gray-500 dark:text-gray-400"}>
                Passwords match
              </span>
            </div>
          </div>

          {error && (
            <div className="text-red-500 text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}

          <Button
            type="submit"
            variant="default"
            className="w-full flex items-center gap-2 justify-center bg-[#1737A2] hover:bg-[#102366]"
            disabled={loading || !allRequirementsMet || !passwordsMatch}
          >
            {loading ? "Updating Password..." : (
              <>
                <KeyRound className="h-5 w-5" />
                Set New Password
              </>
            )}
          </Button>

          {onCancel && (
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={onCancel}
            >
              Cancel
            </Button>
          )}
        </form>
      </div>
    </div>
  );
}
